import React, { useEffect, useRef } from "react";
import Browser from "../browser";
import { Event } from "../models/BaseObject";

var startX = 0;
var startY = 0;
var elm: HTMLElement | null = null;

let lastWidthLandscape = 300;
let lastWidthPortrait = 300;
let lastPointerId = 0;

export let panelResized = new Event();
export let panelResizeActive = false;

export default function PanelResizer(props: {
  leftPanelId?: any;
  bottomPanelId?: any;
  minSize?: number;
  onCollapse?: () => void;
  collapseThreshold?: number;
  style?: React.CSSProperties;
}) {
  const { leftPanelId, bottomPanelId, minSize, onCollapse, collapseThreshold = 24, style: style1 } = props;
  const resizerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelElementRef = useRef<HTMLElement | null>(null);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  function setLastWidth(w: number) {
    if (Browser.isLandScape()) {
      lastWidthLandscape = w;
    } else {
      lastWidthPortrait = w;
    }
  }

  function getLastWidth(): number {
    if (Browser.isLandScape()) {
      return lastWidthLandscape;
    } else {
      return lastWidthPortrait;
    }
  }

  useEffect(() => {
    let orientationTimer: ReturnType<typeof setTimeout> | undefined;

    function onOrientationChange() {
      if (orientationTimer) {
        clearTimeout(orientationTimer);
      }
      orientationTimer = setTimeout(() => {
        orientationTimer = undefined;
        const elm = document.getElementById(leftPanelId);
        if (elm) {
          if (Browser.isLandScape()) {
            elm.style.width = `${lastWidthLandscape}px`;
          } else {
            elm.style.width = `${lastWidthPortrait}px`;
          }
          panelResized.raise(undefined, undefined);
        }
      }, 100);
    }
    window.addEventListener("orientationchange", onOrientationChange);

    return () => {
      window.removeEventListener("orientationchange", onOrientationChange);
      if (orientationTimer) {
        clearTimeout(orientationTimer);
      }
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
        transitionTimerRef.current = undefined;
      }
      if (elm === panelElementRef.current) {
        elm = null;
        panelResizeActive = false;
        lastPointerId = 0;
      }
      panelElementRef.current = null;
    };
  }, []);

  function onPointerMove(event: any) {
    if (panelResizeActive && elm) {
      event.stopPropagation();

      if (leftPanelId) {
        let w = 300;
        if (typeof event.clientX === "number") {
          w = event.clientX;
        } else {
          w = event.touches[0].screenX;
        }

        w -= 5; // half of the resizer width
        const r = elm.getBoundingClientRect();
        w -= r.x;
        if (onCollapse) w = Math.max(0, w);

        elm.style.width = `${w}px`;
        setLastWidth(w);
      } else if (bottomPanelId) {
        let newH = (event.screenY || event.touches[0].screenY) - 5;
        const r = elm.getBoundingClientRect();
        newH -= r.y;
        elm.style.height = `${newH}px`;
      }

      panelResized.raise(undefined, undefined);
    }
  }

  function onPointerDown(event: any) {
    event.stopPropagation();

    if (resizerRef.current) {
      panelResizeActive = true;

      if (leftPanelId) {
        elm = document.getElementById(leftPanelId);
        panelElementRef.current = elm;
        startX = event.screenX || event.touches[0].screenX;
      } else if (bottomPanelId) {
        elm = document.getElementById(bottomPanelId);
        panelElementRef.current = elm;
        startY = event.screenY || event.touches[0].screenY;
      }

      resizerRef.current.setPointerCapture(event.pointerId);
      lastPointerId = event.pointerId;
    }
  }

  function onPointerUp(event: any) {
    event.stopPropagation();

    panelResizeActive = false;
    if (resizerRef.current) {
      resizerRef.current.releasePointerCapture(lastPointerId);
      lastPointerId = 0;
    }

    panelResized.raise(undefined, undefined);
    if (onCollapse && leftPanelId && elm && parseFloat(elm.style.width) <= collapseThreshold) onCollapse();
  }

  function onClick(event: any) {
    if (onCollapse) {
      onCollapse();
      return;
    }
    if (elm) {
      const w = parseInt(elm.style.width);
      elm.style.transition = "width ease-in-out 0.5s";
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
      transitionTimerRef.current = setTimeout(() => {
        transitionTimerRef.current = undefined;
        if (elm) {
          elm.style.transition = "inherit";
        }
      }, 600);

      if (w < 10) {
        let newW = Math.max(getLastWidth(), 300);
        if (!Browser.isLandScape()) {
          newW = window.innerWidth;
        }

        elm.style.width = `${newW}px`;
        setLastWidth(newW);
        if (resizerRef.current) {
          resizerRef.current.style.width = "10px";
        }

        if (buttonRef.current) {
          buttonRef.current.style.marginLeft = `-30px`;
        }

        state.activePanes.noPaneActiveBecauseOnlyNavigationIsVisible = true;
        state.activePanes.raiseOnChange();
      } else {
        elm.style.width = `0px`;
        setLastWidth(0);
        if (resizerRef.current) {
          resizerRef.current.style.width = "0px";
        }
        if (buttonRef.current) {
          buttonRef.current.style.marginLeft = `10px`;
        }
        state.activePanes.noPaneActiveBecauseOnlyNavigationIsVisible = false;
        state.activePanes.raiseOnChange();
      }
      panelResized.raise(undefined, undefined);
    }
  }

  var style: any = {
    position: "relative",
    right: "0px",
    height: "100%",
    width: "10px",
    cursor: "ew-resize", // no itx
    // zIndex: 1,   // MWE 27.5.2024 zindex stört dioe herausragenden Menüs
    flexShrink: "0",
    touchAction: "none",
  };

  if (bottomPanelId) {
    style = {
      position: "relative",
      top: "0px",
      height: "10px",
      width: "100%",
      cursor: "ns-resize", // no itx
      zIndex: 1,
    };
  }

  return (
    <div
      ref={resizerRef}
      onPointerDown={(ev) => onPointerDown(ev)}
      onPointerUp={(ev) => onPointerUp(ev)}
      onPointerMove={(ev) => onPointerMove(ev)}
      className="panelResizer"
      style={{ ...style, ...style1 }}
      onClick={() => {}} // Dieser "leere" onClick Handler ist notwendig, damit auf mobilene Geräten die TouchEvents zum Resizen korrekt funktionieren. MWE 12.1.2024
    >
      {(Browser.isTouch() || Browser.isMobile()) && (
        <div style={{ position: "absolute", bottom: "30px" }}>
          <button
            ref={buttonRef}
            className="btn button"
            style={{
              backgroundColor: "white",
              height: "30px",
              width: "30px",
              borderRadius: "15px",
              marginLeft: "0px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={onClick}
          >
            <i className={`las la-sort la-rotate-90`}></i>
          </button>
        </div>
      )}
    </div>
  );
}
